import { inject } from '@angular/core';
import { ResolveFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TeamMemberService } from '../../../@backoffice/services/team-member.service';
import { buildClientRoute } from '../utils/client-route.util';

/**
 * Interface for invitation token validation result
 */
export interface InvitationTokenValidationResult {
  token: string;
  email: string;
  isValid: boolean;
}

/**
 * Invitation Token Resolver
 * 
 * This resolver validates the invitation token before the register component loads.
 * It extracts token and email from query parameters and validates them via API.
 * 
 * If validation fails or parameters are missing, it redirects to login with an error message.
 * 
 * Usage: Add `resolve: { invitationToken: invitationTokenResolver }` to the route configuration.
 */
export const invitationTokenResolver: ResolveFn<InvitationTokenValidationResult> = (
  route: ActivatedRouteSnapshot
) => {
  const teamMemberService = inject(TeamMemberService);
  const router = inject(Router);

  // Extract token and email from query parameters
  const tokenParam = route.queryParams['token'];
  const emailParam = route.queryParams['email'];

  // Check if required parameters are present
  if (!tokenParam || !emailParam) {
    console.warn('InvitationTokenResolver: Missing token or email in query parameters');
    // Redirect to invalid invitation page (clean URL, no query params)
    router.navigate(buildClientRoute(router, ['/invalid-invitation']));
    return throwError(() => new Error('Missing token or email parameters'));
  }

  // Decode URL-encoded values
  const decodedToken = decodeURIComponent(tokenParam);
  const decodedEmail = decodeURIComponent(emailParam);

  // Validate token via API
  return teamMemberService.validateInvitationToken(decodedToken, decodedEmail).pipe(
    map((response: any) => {
      // Response may be unwrapped by interceptor, so check both structures
      // API returns: { isSuccess: true, data: { success: true, message: "..." } }
      // After unwrapping: { success: true, message: "..." }
      const isSuccess = response?.data?.success === true || 
                       response?.success === true || 
                       response?.isSuccess === true;

      if (isSuccess) {
        // Token is valid - return validation result
        return {
          token: decodedToken,
          email: decodedEmail,
          isValid: true
        };
      } else {
        // Token validation failed - redirect to invalid invitation page
        console.warn('InvitationTokenResolver: Token validation failed - invalid or expired token');
        router.navigate(buildClientRoute(router, ['/invalid-invitation']));
        const validationError = new Error('Invalid or expired invitation token');
        (validationError as any).status = 403; // Mark as 403 for proper error handling
        throw validationError;
      }
    }),
    catchError((error: any) => {
      console.error('InvitationTokenResolver: Error validating invitation token:', error);
      
      // Check if it's a 401/403 error - these mean invalid/expired token
      const statusCode = error?.status || error?.error?.statusCode || error?.statusCode;
      const errorMessage = statusCode === 401 || statusCode === 403
        ? 'Invalid or expired invitation token. Please request a new invitation.'
        : error?.message || 'Failed to validate invitation token. Please try again.';

      // Redirect to invalid invitation page (clean URL, no query params)
      router.navigate(buildClientRoute(router, ['/invalid-invitation']));
      
      return throwError(() => new Error(errorMessage));
    })
  );
};
